import { onlineManager, useQuery } from '@tanstack/react-query';
import { useRef, useSyncExternalStore } from 'react';

import type { SessionSnapshot } from '@/contracts/onboarding-session';
import { HttpError } from '@/features/onboarding/services/http-client';

import { hasActiveJobs } from '../state/buildTranscript';
import { getSession, putSession } from '../services/onboarding-session.service';
import { onboardingKeys } from './queryKeys';

/** The poll interval while everything is fine. */
export const SNAPSHOT_POLL_MS = 2000;
/**
 * The poll interval once every job is terminal. Slow, but not off: it is how a tab that sat idle notices the BFF
 * forgot the session (restart, evicted key) and re-PUTs it before the person's next click hits a 404.
 */
export const SNAPSHOT_HEARTBEAT_MS = 20_000;
/** Retry delays after consecutive failures: 1s, then 3s, then 8s for every failure after. */
export const SNAPSHOT_BACKOFF_MS = [1000, 3000, 8000] as const;

/** The poll delay for the Nth consecutive failure (0 = healthy). */
export function snapshotPollDelay(consecutiveFailures: number): number {
  if (consecutiveFailures <= 0) return SNAPSHOT_POLL_MS;
  return SNAPSHOT_BACKOFF_MS[Math.min(consecutiveFailures, SNAPSHOT_BACKOFF_MS.length) - 1]!;
}

/** The BFF forgot this session (restart with an in-memory store, or an evicted Redis key). */
export function isSessionNotFound(error: unknown): boolean {
  return error instanceof HttpError && error.status === 404 && error.envelope?.error?.code === 'session_not_found';
}

/**
 * The BFF answered the re-PUT of a session it had just reported missing with 404 again. It gives that same answer
 * for an id that belongs to another operator, so this id can never be ours: the caller rotates it.
 */
export class SessionReattachRefusedError extends Error {
  constructor(public readonly cause: unknown) {
    super('The session id cannot be re-attached');
    this.name = 'SessionReattachRefusedError';
  }
}

export const isReattachRefused = (error: unknown): boolean => error instanceof SessionReattachRefusedError;

/**
 * GET the snapshot; when the BFF has lost the session (404 `session_not_found`) and the url is known, re-PUT the
 * same id and url so it re-attaches. A 404 on that re-PUT too is a `SessionReattachRefusedError`. Shared by the
 * poll and by the recovery of a failed mutation or query.
 */
export async function fetchSnapshot(sessionId: string, url: string | null): Promise<SessionSnapshot> {
  try {
    return await getSession(sessionId);
  } catch (error) {
    if (!(isSessionNotFound(error) && url)) throw error;
    try {
      return await putSession(sessionId, url);
    } catch (putError) {
      throw isSessionNotFound(putError) ? new SessionReattachRefusedError(putError) : putError;
    }
  }
}

export interface UseSessionSnapshotOptions {
  sessionId: string;
  /** The URL the session was created with: re-PUT when the BFF has lost the session. */
  url: string | null;
  /** Off until the URL was accepted and the first PUT settled. */
  enabled: boolean;
}

export interface SessionSnapshotApi {
  snapshot: SessionSnapshot | null;
  /** True while the browser is offline, or from the first failed poll until one succeeds: the "Reconnecting…" banner. */
  reconnecting: boolean;
  /** The last poll's error, or null while it is healthy. */
  error: unknown;
  isLoading: boolean;
}

/**
 * GET /sessions/:id on a 2s poll, with 1/3/8s backoff while it fails. On 404 `session_not_found` it re-PUTs
 * the same id and url, so the BFF re-attaches (the jobs start again; acceptable in mock mode, and connect
 * mode requires a persistent store). Once every job is terminal it slows to a 20s heartbeat instead of
 * stopping, so a forgotten session is noticed. Neither poll runs in a hidden tab.
 */
export function useSessionSnapshot({ sessionId, url, enabled }: UseSessionSnapshotOptions): SessionSnapshotApi {
  const urlRef = useRef(url);
  urlRef.current = url;
  // Consecutive failures, counted here: TanStack's own fetchFailureCount restarts at 0 on every fetch, so with
  // `retry: false` it can never tell a third failure from a first.
  const failuresRef = useRef(0);

  const query = useQuery({
    queryKey: onboardingKeys.snapshot(sessionId),
    enabled: enabled && url !== null,
    // The retry is the poll itself, with its own backoff.
    retry: false,
    staleTime: 0,
    gcTime: Infinity,
    queryFn: async () => {
      try {
        const result = await fetchSnapshot(sessionId, urlRef.current);
        failuresRef.current = 0;
        return result;
      } catch (error) {
        failuresRef.current += 1;
        throw error;
      }
    },
    refetchInterval: (q) => {
      const failures = failuresRef.current;
      if (failures > 0) return snapshotPollDelay(failures);
      return q.state.data && !hasActiveJobs(q.state.data) ? SNAPSHOT_HEARTBEAT_MS : SNAPSHOT_POLL_MS;
    },
    refetchIntervalInBackground: false,
  });

  // TanStack pauses queries while the browser is offline (networkMode 'online'), so the poll never fails and
  // isError stays false: read the online state directly so going offline still shows the banner.
  const online = useSyncExternalStore(onlineManager.subscribe.bind(onlineManager), () => onlineManager.isOnline());

  return {
    snapshot: query.data ?? null,
    reconnecting: query.isError || (enabled && url !== null && !online),
    error: query.error,
    isLoading: query.isLoading,
  };
}
